// An occupancy bar (reservation / inquiry / owner block) is 30px tall with a
// 5px gap, so the next lane down starts this far below.
export const OCCUPANCY_LANE_STEP = 35;
// Clearance a job card needs below a single lane of occupancy bars that
// overflow into its day cell from another cell.
export const OCCUPANCY_BAND_HEIGHT = 44;
// Gap above a job card when nothing overflows into its day cell.
export const DEFAULT_JOB_PADDING_TOP = 5;

export interface OccupancyBar {
  key: string;
  /** Weekday column index of the bar's first rendered day. */
  start: number;
  /** Weekday column index of the bar's last rendered day (inclusive). */
  end: number;
}

export interface OccupancyPlacement {
  lane: number;
  offset: number;
}

/**
 * Lane (vertical row) for every occupancy bar in a property row, plus the
 * margin-top offset that puts the bar in that lane.
 *
 * `bars` must be in render order: the template stacks the bars that start in
 * a day cell in normal flow, in that order.
 *
 * Bars that overlap in time must not paint on top of each other, so each takes
 * the first lane whose bars all finish before it starts (greedy interval
 * partitioning across the weekday columns). Bars that only touch (one ends
 * the day the next starts) share that day cell half-and-half, so they may
 * share a lane — unless both start in that same cell. Flow stacks those, so
 * they can never sit on one row; a stay clamped to the period start followed
 * by a same-day check-in is the common case.
 *
 * Because a bar's lane is the row it really renders in, its offset is just
 * the rows skipped since the bar rendered before it in the same cell. The
 * offset is applied as margin-top (not top) so the lane also reserves its
 * space: jobs below it flow down and the row grows.
 */
export function layoutOccupancyLanes(bars: OccupancyBar[]): Map<string, OccupancyPlacement> {
  const lanes = new Map<string, number>();
  const laneStarts: number[] = [];
  const laneEnds: number[] = [];
  bars
    .map((bar, renderIndex) => ({ bar, renderIndex }))
    .sort((a, b) => a.bar.start - b.bar.start || a.renderIndex - b.renderIndex)
    .forEach(({ bar }) => {
      let lane = laneEnds.findIndex(
        (lastEnd, i) => lastEnd < bar.start || (lastEnd === bar.start && laneStarts[i] < bar.start)
      );
      if (lane === -1) {
        lane = laneEnds.length;
      }
      laneStarts[lane] = bar.start;
      laneEnds[lane] = bar.end;
      lanes.set(bar.key, lane);
    });

  const previousLaneByCell = new Map<number, number>();
  const layout = new Map<string, OccupancyPlacement>();
  bars.forEach((bar) => {
    const lane = lanes.get(bar.key) ?? 0;
    const previousLane = previousLaneByCell.get(bar.start) ?? -1;
    previousLaneByCell.set(bar.start, lane);
    layout.set(bar.key, {
      lane,
      offset: Math.max(0, lane - previousLane - 1) * OCCUPANCY_LANE_STEP,
    });
  });
  return layout;
}

/**
 * Lanes a job card must clear on a weekday column: the deepest lane holding a
 * bar over that day, plus one. Zero when nothing covers the day. Counting the
 * bars instead would miss the empty lanes above a lane-1 bar.
 */
export function getOccupancyDepthOnDay(
  bars: OccupancyBar[],
  layout: Map<string, OccupancyPlacement>,
  dayIndex: number
): number {
  return bars.reduce((depth, bar) => {
    const placement = layout.get(bar.key);
    if (!placement || bar.start > dayIndex || dayIndex > bar.end) {
      return depth;
    }
    return Math.max(depth, placement.lane + 1);
  }, 0);
}

/** Lanes already filled by the bars that start in (and so flow inside) a day cell. */
export function getOccupancyDepthStartingOnDay(
  bars: OccupancyBar[],
  layout: Map<string, OccupancyPlacement>,
  dayIndex: number
): number {
  return getOccupancyDepthOnDay(
    bars.filter((bar) => bar.start === dayIndex),
    layout,
    dayIndex
  );
}

/**
 * padding-top for the first job card in a day cell so it clears every bar over
 * that day. Bars starting in the cell sit above the job in normal flow and
 * already push it down `flowDepth` lanes; bars extending in from earlier
 * cells do not, so the job pads past the deepest of them.
 */
export function getJobClearance(depth: number, flowDepth: number): number {
  if (depth <= flowDepth) {
    return DEFAULT_JOB_PADDING_TOP;
  }
  return OCCUPANCY_BAND_HEIGHT + (depth - flowDepth - 1) * OCCUPANCY_LANE_STEP;
}
